import styled from "@emotion/styled";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

import { paper, fonts, bp } from "../theme";
import { useWebSocket } from "../useWebSocket";
import { useGameState } from "../GameStateContext";
import { getPlayerName } from "../players";
import type { PlayerInfo, VotingView } from "../protocol";

import {
    MsScreen, MsBody, MsFooter,
    MsDisplay, MsEyebrow, MsChip,
    MsQuote, MsQmark, MsQtext, MsSticky, MsBtn,
    MaskHeadline,
    ServerTimerRule,
    Stack,
} from "../ms";


const EntriesGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-top: 6px;

    @media ${bp.wide} {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: 22px;
    }
`;

const AwaitingChips = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 6px;
    margin-top: 4px;
`;

const VotingBody = styled(MsBody)`
    gap: 14px;
    padding-top: 14px;
`;

const PromptDisplay = styled(MsDisplay)`
    font-size: 30px;
`;

const OwnBadge = styled.div`
    position: absolute;
    top: 12px;
    right: 12px;
`;

const VotedBadge = styled.div`
    position: absolute;
    bottom: 12px;
    right: 12px;
`;

const VotedLabel = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: ${fonts.sans};
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${paper.sticky};
`;

const AwaitingStack = styled(Stack)`
    margin-top: 12px;
`;


interface Props {
    view: VotingView;
    players: PlayerInfo[];
    self: PlayerInfo;
}

export default function Voting({ view, players, self }: Props) {
    const { sendMessage } = useWebSocket();
    const { state } = useGameState();
    const { entries, hasVoted, awaitingPlayers } = view;
    const [chosen, setChosen] = useState<string | null>(null);

    const handlePick = (playerId: string) => {
        if (hasVoted || playerId === self?.id) return;
        setChosen(playerId);
        setTimeout(() => sendMessage({ type: "cast_vote", playerId }), 600);
    };

    const ordered = Object.entries(entries) as Array<[string, string | null]>;

    return (
        <MsScreen>
            <ServerTimerRule
                timeoutTime={state.common?.timeoutTime}
                phaseStartTime={state.common?.phaseStartTime}
                warnAt={8}
            />
            <VotingBody wide>
                <div>
                    <PromptDisplay>
                        <MaskHeadline>
                            {hasVoted ? "Vote in. Awaiting others." : "Pick the best misquote."}
                        </MaskHeadline>
                    </PromptDisplay>
                </div>
                <EntriesGrid>
                    {ordered.map(([playerId, answer], i) => {
                        const isOwn = playerId === self?.id;
                        const voted = chosen === playerId;
                        const tilt = i % 2 === 0 ? "-0.6deg" : "0.6deg";
                        return (
                            <motion.div
                                key={playerId}
                                layoutId={voted ? "winning-quote" : undefined}
                                style={{ transform: `rotate(${tilt})` }}
                            >
                                <MsQuote
                                    voted={voted}
                                    disabled={hasVoted || isOwn}
                                    onClick={() => handlePick(playerId)}
                                    style={{ transform: "none" }}
                                >
                                    <MsQmark voted={voted}>"</MsQmark>
                                    <MsQtext>{answer || "(no answer)"}</MsQtext>
                                    {isOwn && (
                                        <OwnBadge>
                                            <MsSticky>your entry</MsSticky>
                                        </OwnBadge>
                                    )}
                                    {voted && (
                                        <VotedBadge>
                                            <VotedLabel>✓ Voted</VotedLabel>
                                        </VotedBadge>
                                    )}
                                </MsQuote>
                            </motion.div>
                        );
                    })}
                </EntriesGrid>
                {awaitingPlayers.length > 0 && (
                    <AwaitingStack gap={8} align="center">
                        <MsEyebrow tone="mute">
                            Awaiting · {awaitingPlayers.length}
                        </MsEyebrow>
                        <AwaitingChips>
                            <AnimatePresence>
                                {awaitingPlayers.map(p => (
                                    <MsChip
                                        key={p.id}
                                        size="sm"
                                        layout
                                        initial={{ opacity: 0, scale: 0.85, y: 8 }}
                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                        exit={{ opacity: 0, scale: 0.85, y: -8 }}
                                    >
                                        {getPlayerName(p.id, players)}
                                    </MsChip>
                                ))}
                            </AnimatePresence>
                        </AwaitingChips>
                    </AwaitingStack>
                )}
            </VotingBody>
            <MsFooter wide>
                {hasVoted && (
                    <MsBtn variant="ghost" disabled>Vote cast. Counting…</MsBtn>
                )}
            </MsFooter>
        </MsScreen>
    );
}
